import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Page,
  Layout,
  Card,
  Form,
  TextField,
  Select,
  Button,
  Toast,
  DatePicker,
  Banner,
  List,
} from '@shopify/polaris';
import { SaveBar, useAppBridge } from '@shopify/app-bridge-react';
import useAxiosClient from '../axiosClient';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../../i18n';

export default function EditTimeOff() {
  const navigate = useNavigate();
  const axiosClient = useAxiosClient();
  const appBridge = useAppBridge();
  const { id } = useParams();
  const { t } = useTranslation();

  const [timeOffInfo, setTimeOffInfo] = useState({
    staff_id: '',
    policy_id: '',
    location_id: '',
    startDate: null,
    endDate: null,
    reason: '',
  });

  const [staffList, setStaffList] = useState([]);
  const [policyList, setPolicyList] = useState([]);
  const [locationList, setLocationList] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isReady, setIsReady] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastContent, setToastContent] = useState('');
  const [errors, setErrors] = useState({});
  const originalData = useRef(timeOffInfo);

  const toSelectId = (value) => (value === null || value === undefined || value === '' ? '' : String(value));

  const parseLocalDate = (dateStr) => {
    if (!dateStr) return null;
    const normalized = String(dateStr).split('T')[0];
    const [year, month, day] = normalized.split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  };

  const unwrapList = (payload, keys) => {
    if (Array.isArray(payload)) return payload;
    for (const key of keys) {
      if (Array.isArray(payload?.[key])) return payload[key];
    }
    return [];
  };

  const ensureStaffOption = (list, timeOff) => {
    const staffId = toSelectId(timeOff?.staff_id);
    if (!staffId) return list;

    if (list.some((member) => toSelectId(member.id) === staffId)) {
      return list;
    }

    return [
      ...list,
      {
        id: timeOff.staff_id,
        first_name: timeOff.staff_first_name || 'Staff',
        last_name: timeOff.staff_last_name || `#${staffId}`,
      },
    ];
  };

  const ensurePolicyOption = (list, timeOff) => {
    const policyId = toSelectId(timeOff?.policy_id);
    if (!policyId) return list;

    if (list.some((policy) => toSelectId(policy.id) === policyId)) {
      return list;
    }

    return [
      ...list,
      {
        id: timeOff.policy_id,
        policy_name: timeOff.policy_name || `Policy #${policyId}`,
      },
    ];
  };

  const ensureLocationOption = (list, timeOff) => {
    const locationId = toSelectId(timeOff?.location_id);
    if (!locationId) return list;

    if (list.some((location) => toSelectId(location.id) === locationId)) {
      return list;
    }

    return [
      ...list,
      {
        id: timeOff.location_id,
        name: `Location #${locationId}`,
      },
    ];
  };

  const [selectedDates, setSelectedDates] = useState({
    start: new Date(),
    end: new Date(),
  });

  const [{ month, year }, setDate] = useState({
    month: new Date().getMonth(),
    year: new Date().getFullYear(),
  });

  const handleMonthChange = useCallback(
    (month, year) => setDate({ month, year }),
    []
  );

  const loadPageData = async () => {
    try {
      setIsLoading(true);
      setIsReady(false);

      const results = await Promise.allSettled([
        axiosClient.get('/getStaff'),
        axiosClient.get('/getPolicies'),
        axiosClient.get('/locations'),
        axiosClient.get(`/fetchTimeOff/${id}`),
      ]);

      const [staffResult, policyResult, locationResult, timeOffResult] = results;

      if (timeOffResult.status !== 'fulfilled' || !timeOffResult.value.data?.success) {
        setToastContent(t('failed_to_load_time_off_details'));
        setShowToast(true);
        return;
      }

      const timeOff = timeOffResult.value.data.timeOff;

      let staff = staffResult.status === 'fulfilled' && staffResult.value.data?.success
        ? unwrapList(staffResult.value.data, ['staff', 'data'])
        : [];
      if (staffResult.status !== 'fulfilled' || !staffResult.value.data?.success) {
        setToastContent(t('failed_to_load_staff_list'));
        setShowToast(true);
      }

      let policies = policyResult.status === 'fulfilled' && policyResult.value.data?.success
        ? unwrapList(policyResult.value.data, ['policies', 'data'])
        : [];
      if (policyResult.status !== 'fulfilled' || !policyResult.value.data?.success) {
        setToastContent(t('failed_to_load_policies'));
        setShowToast(true);
      }

      let locations = locationResult.status === 'fulfilled' && locationResult.value.data?.success
        ? unwrapList(locationResult.value.data, ['locations', 'data'])
        : [];
      if (locationResult.status !== 'fulfilled' || !locationResult.value.data?.success) {
        setToastContent(t('failed_to_load_locations'));
        setShowToast(true);
      }

      staff = ensureStaffOption(staff, timeOff);
      policies = ensurePolicyOption(policies, timeOff);
      locations = ensureLocationOption(locations, timeOff);

      const startDate = parseLocalDate(timeOff.start_date);
      const endDate = parseLocalDate(timeOff.end_date);

      const loadedInfo = {
        staff_id: toSelectId(timeOff.staff_id),
        policy_id: toSelectId(timeOff.policy_id),
        location_id: toSelectId(timeOff.location_id),
        startDate,
        endDate,
        reason: timeOff.reason || '',
      };

      setStaffList(staff);
      setPolicyList(policies);
      setLocationList(locations);
      setTimeOffInfo(loadedInfo);
      originalData.current = loadedInfo;

      if (startDate && endDate) {
        setSelectedDates({ start: startDate, end: endDate });
        setDate({ month: startDate.getMonth(), year: startDate.getFullYear() });
      }

      setIsReady(true);
    } catch (error) {
      setToastContent(t('error_loading_time_off_details'));
      setShowToast(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      loadPageData();
    }
  }, [id]);

  const staffOptions = useMemo(
    () => staffList.map((staff) => ({
      label: `${staff.first_name || ''} ${staff.last_name || ''}`.trim() || `Staff #${toSelectId(staff.id)}`,
      value: toSelectId(staff.id),
    })),
    [staffList]
  );

  const policyOptions = useMemo(
    () => policyList.map((policy) => ({
      label: policy.policy_name || `Policy #${toSelectId(policy.id)}`,
      value: toSelectId(policy.id),
    })),
    [policyList]
  );

  const locationOptions = useMemo(
    () => locationList.map((location) => ({
      label: location.name || `Location #${toSelectId(location.id)}`,
      value: toSelectId(location.id),
    })),
    [locationList]
  );

  const handleChange = (field) => (value) => {
    setTimeOffInfo((prev) => ({
      ...prev,
      [field]: value,
    }));
    setIsDirty(true);

    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!timeOffInfo.staff_id) {
      newErrors.staff_id = t('please_select_a_staff_member');
    }

    if (!timeOffInfo.policy_id) {
      newErrors.policy_id = t('please_select_a_policy');
    }

    if (!timeOffInfo.location_id) {
      newErrors.location_id = t('please_select_a_location');
    }

    if (!timeOffInfo.startDate) {
      newErrors.startDate = t('start_date_is_required');
    }

    if (!timeOffInfo.endDate) {
      newErrors.endDate = t('end_date_is_required');
    } else if (timeOffInfo.startDate && new Date(timeOffInfo.endDate) < new Date(timeOffInfo.startDate)) {
      newErrors.endDate = t('end_date_cannot_be_before_start_date');
    }

    if (!timeOffInfo.reason || timeOffInfo.reason.trim().length < 10) {
      newErrors.reason = t('reason_is_required_and_must_be_at_least_10_characters');
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      setShowToast(true);
      setToastContent(t('please_fill_in_all_required_fields_correctly'));
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setIsSaving(true);

      const payload = {
        staff_id: timeOffInfo.staff_id,
        policy_id: timeOffInfo.policy_id,
        location_id: timeOffInfo.location_id,
        start_date: timeOffInfo.startDate ? new Date(timeOffInfo.startDate).toLocaleDateString('en-US') : null,
        end_date: timeOffInfo.endDate ? new Date(timeOffInfo.endDate).toLocaleDateString('en-US') : null,
        reason: timeOffInfo.reason,
      };

      if (!payload.staff_id) {
        setErrors(prev => ({ ...prev, staff_id: t('could_not_find_staff_id') }));
        setShowToast(true);
        setToastContent(t('staff_id_could_not_be_determined'));
        setIsSaving(false);
        return;
      }

      if (!payload.policy_id) {
        setErrors(prev => ({ ...prev, policy_id: t('could_not_find_policy_id') }));
        setShowToast(true);
        setToastContent(t('policy_id_could_not_be_determined'));
        setIsSaving(false);
        return;
      }

      if (!payload.location_id) {
        setErrors(prev => ({ ...prev, location_id: t('could_not_find_location_id') }));
        setShowToast(true);
        setToastContent(t('location_id_could_not_be_determined'));
        setIsSaving(false);
        return;
      }

      const response = await axiosClient.post(`/update_timeoff/${id}`, payload);

      if (response.data.success) {
        originalData.current = timeOffInfo;
        setIsDirty(false);
        setToastContent(t('time_off_updated_successfully'));
        setShowToast(true);
        navigate('/TimeOff');
        appBridge.saveBar.hide('edit-timeoff-save-bar');
      } else {
        setToastContent(response.data.message || t('failed_to_update_time_off'));
        setShowToast(true);
        appBridge.saveBar.hide('edit-timeoff-save-bar');
      }
    } catch (error) {
      // Handle validation errors from the backend
      if (error.response && error.response.data && error.response.data.errors) {
        setErrors(error.response.data.errors);
        setToastContent(error.response.data.message || t('validation_error'));
      } else {
        setToastContent(t('error_updating_time_off'));
      }

      setShowToast(true);
      appBridge.saveBar.hide('edit-timeoff-save-bar');
    } finally {
      setIsSaving(false);
      appBridge.saveBar.hide('edit-timeoff-save-bar');
    }
  };

  const handleDiscard = () => {
    setTimeOffInfo(originalData.current);
    setIsDirty(false);
    setErrors({});
    appBridge.saveBar.hide('edit-timeoff-save-bar');
  };

  if (isLoading || !isReady) {
    return (
      <Page title={t('edit_time_off')} fullWidth>
        <Layout>
          <Layout.Section>
            <Card sectioned>
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <p>{t('loading_time_off_details')}</p>
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title={t('edit_time_off')}
      fullWidth
      backAction={{
        content: t('time_off_management'),
        onAction: () => navigate('/TimeOff'),
      }}
    >
      {Object.keys(errors).length > 0 && (
        <Banner title={t('please_correct_the_following_errors')} tone="critical">
          <List type="bullet">
            {Object.entries(errors).map(([field, message]) => (
              <List.Item key={field}>
                {`${field.charAt(0).toUpperCase() + field.slice(1)}: ${message}`}
              </List.Item>
            ))}
          </List>
        </Banner>
      )}

      <Form onSubmit={handleSave}>
        <Layout>
          {/* Select Staff */}
          <Layout.AnnotatedSection
            title={t('select_staff')}
            description={t('choose_the_staff_member_requesting_time_off')}
          >
            <Card sectioned>
              <Select
                key={`staff-${timeOffInfo.staff_id}-${staffOptions.length}`}
                label={t('staff')}
                options={staffOptions}
                value={timeOffInfo.staff_id}
                onChange={handleChange('staff_id')}
                placeholder={t('select_staff')}
                error={errors.staff_id}
              />
            </Card>
          </Layout.AnnotatedSection>

          {/* Select Policy */}
          <Layout.AnnotatedSection
            title={t('select_policy')}
            description={t('choose_the_policy_applicable_for_this_time_off')}
          >
            <Card sectioned>
              <Select
                key={`policy-${timeOffInfo.policy_id}-${policyOptions.length}`}
                label={t('policy')}
                options={policyOptions}
                value={timeOffInfo.policy_id}
                onChange={handleChange('policy_id')}
                placeholder={t('select_policy')}
                error={errors.policy_id}
              />
            </Card>
          </Layout.AnnotatedSection>
          
          {/* Select Location */}
          <Layout.AnnotatedSection
            title={t('select_location')}
            description={t('choose_the_location_for_this_time_off')}
          >
            <Card sectioned>
              <Select
                key={`location-${timeOffInfo.location_id}-${locationOptions.length}`}
                label={t('location')}
                options={locationOptions}
                value={timeOffInfo.location_id}
                onChange={handleChange('location_id')}
                placeholder={t('select_location')}
                error={errors.location_id}
              />
            </Card>
          </Layout.AnnotatedSection>

          {/* Select Dates */}
          <Layout.AnnotatedSection
            title={t('select_dates')}
            description={t('specify_the_start_and_end_dates_for_the_time_off')}
          >
            <Card sectioned>
              <DatePicker
                month={month}
                year={year}
                onChange={(value) => {
                  setSelectedDates(value);
                  setTimeOffInfo((prev) => ({
                    ...prev,
                    startDate: value.start,
                    endDate: value.end,
                  }));
                  setIsDirty(true);
                }}
                onMonthChange={handleMonthChange}
                selected={selectedDates}
                allowRange
              />
              {errors.startDate && <p style={{ color: 'red' }}>{errors.startDate}</p>}
              {errors.endDate && <p style={{ color: 'red' }}>{errors.endDate}</p>}
            </Card>
          </Layout.AnnotatedSection>

          {/* Reason */}
          <Layout.AnnotatedSection
            title={t('reason')}
            description={t('provide_a_reason_for_the_time_off_request')}
          >
            <Card sectioned>
              <TextField
                label={t('reason')}
                value={timeOffInfo.reason}
                onChange={handleChange('reason')}
                placeholder={t('enter_reason_for_time_off')}
                error={errors.reason}
                multiline={4}
              />
            </Card>
          </Layout.AnnotatedSection>
        </Layout>
      </Form>

      {isDirty && (
        <SaveBar
          id="edit-timeoff-save-bar"
          open={isDirty}
        >
          <button
            type="button"
            variant="primary"
            onClick={async () => {
              try {
                await handleSave();
              } catch (err) {
                // Error handled silently
              }
            }}
            disabled={isSaving}
          >
            {isSaving ? t('saving') : t('save')}
          </button>
          <button
            type="button"
            onClick={handleDiscard}
            disabled={isSaving}
          >
            {t('discard')}
          </button>
        </SaveBar>
      )}
    </Page>
  );
}
